import {Component, OnInit, Input, Output, EventEmitter, ViewChild} from "@angular/core";
import {FormBuilder, FormGroup, Validators, FormControl} from "@angular/forms";
import {Message} from "../../shared/api/messages/api.models";
import {Attachment, Blob} from "../../shared/api/attachments/api.models";
import {Api} from "../../shared/api.service";
import { Tenant, TenantService } from '../../shared/tenant.service';

@Component({
    selector : 'message',
    template: require('./message.component.html'),
    styles: [require('./message.component.scss')]
})

export class MessageComponent implements OnInit {
    @ViewChild('body') body;

    @Input()
    settings: any = {
        title: null,
        showMessageType: false,
        showMessageGender: false
    };

    @Input()
    message: Message;

    @Input()
    editing: boolean;

    @Output()
    messageUpdated = new EventEmitter();

    @ViewChild('attachmentInput') attachmentInput;

    private latestInputElement: HTMLInputElement;

    protected internalSettings: any = {
        showSubject     : true,
        showBody        : true,
        showVariables   : true,
        showAttachments : true
    };

    public messageTypes: string[] = Message.types;
    public messageForm: FormGroup;

    public messageContent: string;

    public attachmentChanged: boolean = false;
    public oldAttachment: Blob;

    public messageTitles: string[] = [
        'First message',
        'Second message',
        'Third message'
    ];

    public variables: any = {
        sender: [
            'fromName',
            'fromAge',
            'fromRealName'
        ],
        receiver: [
            'hairColor',
            'eyeColor',
            'build',
            'education',
            'city',
            'province',
            'country',
            'name',
            'age',
            'gender'
        ],
        common: [
            'dayOfWeek',
        ]
    };

    public geoWizardPokeVariables: string[] = ['playerCity', 'playerProvince'];

    attachment: any = {
        restrictions : {
            maxSize     : 20971520,
            allowedMime : [
                'image/png',
                'image/jpeg',
                'image/jpg'
            ],
            minWidth  : 300,
            minHeight : 300
        },
        current : {
            error  : null,
            mime   : null,
            size   : null,
            width  : null,
            height : null,
            filename : null
        }
    };

    constructor(private fb: FormBuilder, private api: Api, private tenantService: TenantService) {}

    ngOnInit() {
        
        if (this.tenantService.getTenant() === Tenant.ULTRA_MEDIA) {
            this.variables.sender.push(...this.geoWizardPokeVariables);
        }

        this.messageForm = this.fb.group({
            id             : [this.message.id],
            type           : [this.message.type, [Validators.required]],
            subject        : [this.message.subject, this.getSubjectValidatorsForMessageType(this.message.type)],
            content        : [this.message.content, this.getBodyValidatorsForMessageType(this.message.type)],
            attachmentType : [this.message.attachmentType, Validators.required],
            attachmentData : [this.message.attachmentData, this.getAttachmentDataValidatorsForType(this.message.attachmentType)]
        });

        if (this.message.attachmentId && this.message.attachmentType === Attachment.TYPE_CUSTOM) {
            this.api.attachments().blob(this.message.attachmentId).subscribe(
                (blob: Blob) => {
                    this.oldAttachment = blob;

                    this.messageForm.controls['attachmentData'].setValue(blob.content);
                    this.messageForm.updateValueAndValidity();
                }
            )
        }

        this.messageForm.controls['type'].valueChanges.subscribe((type: string) => {
            this.messageForm.controls['subject'].setValidators(this.getSubjectValidatorsForMessageType(type));
            this.messageForm.controls['content'].setValidators(this.getBodyValidatorsForMessageType(type));
            this.messageForm.controls['subject'].updateValueAndValidity();
            this.messageForm.controls['content'].updateValueAndValidity();
            if (type !== Message.TYPE_NORMAL) {
                this.messageForm.controls['attachmentType'].setValue(Attachment.TYPE_NONE);
                this.resetAttachment();
            }
        });

        this.messageForm.controls['attachmentType'].valueChanges.subscribe((type: string) => {
            this.messageForm.controls['attachmentData'].setValidators(this.getAttachmentDataValidatorsForType(type));
            this.messageForm.controls['attachmentData'].updateValueAndValidity();
            this.messageForm.updateValueAndValidity();

            if (type !== Attachment.TYPE_CUSTOM) {
                this.resetAttachment();
            }
        });

        this.messageForm.valueChanges.subscribe((message: Message) => {
            this.setSettingsBasedOnMessageType(message.type);
            this.resetSubjectAndContentBasedOnType(message);

            Object.assign(this.message, message);

            this.messageUpdated.emit(this.messageForm.valid);

        });

        this.setSettingsBasedOnMessageType(this.message.type);

        this.messageForm.updateValueAndValidity();
    }

    private resetSubjectAndContentBasedOnType(message: Message) {
        if (message.type !== Message.TYPE_NORMAL) {
            message.subject = null;
            message.content = null;
        }
    }

    trimFields() {
        if (this.messageForm.controls['subject'].value) {
            this.messageForm.controls['subject'].setValue(this.messageForm.controls['subject'].value.trim());
            this.messageForm.controls['subject'].updateValueAndValidity();
        }

        if (this.messageForm.controls['content'].value) {
            this.messageForm.controls['content'].setValue(this.messageForm.controls['content'].value.trim());
            this.messageForm.controls['content'].updateValueAndValidity();
        }
    }

    validateInput() {
        if (!this.messageForm) {
            return true;
        }

        const contentValue = this.messageForm.controls['content'].value;
        const subjectValue = this.messageForm.controls['subject'].value;
        const regex = /(%\[|%|\[)([^\]%\s]+)(\]%|\]|%|\s|$)/g;
        const content = contentValue + subjectValue;
        const variables = this.variables;
        let variableArray = [];
        let isValid = true;

        Object.keys(variables).forEach(function(varSegment) {
            variables[varSegment].forEach(function(variable) {
                variableArray.push(variable);
            })
        })

        if (content) {
            content.replace(regex, function():any {
                if (variableArray.indexOf(arguments[2]) === -1) {
                    // unknownVariable
                    isValid = false;
                } else if (arguments[1] !== '%[' || arguments[3] !== ']%') {
                    // syntaxError
                    isValid = false;
                }
            });
        }

        return isValid;
    }

    newAttachment(file: File) {
        let maxSize   = this.attachment.restrictions.maxSize;
        let minHeight = this.attachment.restrictions.minHeight;
        let minWidth  = this.attachment.restrictions.minWidth;

        this.resetAttachment();

        this.attachment.current.filename = file.name;
        this.attachment.current.size = file.size;
        this.attachment.current.mime = file.type;

        // Check if the file is not exceeding the maximum limit
        if (file.size > maxSize) {
            this.attachment.current.error = 'File is ' + file.size + 'bytes. A maxmium of ' + maxSize + ' bytes is allowed.';
            return;
        }

        // Check if the mime type is correct
        // @TODO Check the header. file.type is based on extension.... Bad!
        if (this.attachment.restrictions.allowedMime.indexOf(file.type) === -1) {
            this.attachment.current.error = 'File ' + file.name + ' should be a jpg or png.';
            return;
        }

        let reader = new FileReader();

        reader.addEventListener('load', () => {
            // Load image for dimensions
            let image = new Image();
            image.src = reader.result;

            image.onload = () => {
                // Set the known dimensions
                this.attachment.current.width  = image.width;
                this.attachment.current.height = image.height;

                // Check if the dimensions restrictions are met
                if (image.height < minHeight || image.width < minWidth) {
                    this.attachment.current.error = image.width + ' x ' + image.height + ' is too small. Minimal ' + minWidth + ' x ' + minHeight;
                    return;
                }

                this.messageForm.controls['attachmentData'].setValue(reader.result);
            };
        }, false);

        reader.readAsDataURL(file);
    }

    delAttachment(event: Event) {
        event.preventDefault();

        this.resetAttachment();
    }

    restoreAttachment(event: Event) {
        event.preventDefault();

        this.resetAttachment();

        this.messageForm.controls['attachmentData'].setValue(this.oldAttachment.content);
        this.attachmentChanged = false;
    }

    setLatestFocus(element: HTMLInputElement) {
        this.latestInputElement = element;
    }

    addVariable(event: Event, element: HTMLInputElement, variable: string) {
        event.preventDefault();

        if (!this.latestInputElement) {
            this.setLatestFocus(element);
        }

        let caretPosition  = this.latestInputElement.selectionStart;
        let control        = this.messageForm.controls[this.latestInputElement.attributes.getNamedItem('formcontrolname').value] as FormControl;
        let value          = control.value;
        let firstPart      = '';
        let lastPart       = '';

        if (value) {
            firstPart = value.substring(0, caretPosition);
            lastPart  = value.substring(caretPosition, value.length);
        }

        control.setValue(firstPart + ' %[' + variable + ']% ' + lastPart);
        this.latestInputElement.scrollTop = caretPosition + 6 + variable.length;
        this.latestInputElement.focus();

        return false;
    }

    private setSettingsBasedOnMessageType(type: string) {
        this.internalSettings.showSubject     = true;
        this.internalSettings.showBody        = true;
        this.internalSettings.showVariables   = true;
        this.internalSettings.showAttachments = true;

        if (type !== Message.TYPE_NORMAL) {
            this.internalSettings.showSubject     = false;
            this.internalSettings.showBody        = false;
            this.internalSettings.showVariables   = false;
            this.internalSettings.showAttachments = false;
        }
    }

    private getSubjectValidatorsForMessageType(type: string) {
        switch (type) {
            case Message.TYPE_NORMAL:
                return [Validators.required, Validators.minLength(4), Validators.maxLength(85), () => {
                    if (this.validateInput()) {
                        return null;
                    }

                    return { validateVariables : { valid: false } };
                }];
            default:
                return [];
        }
    }

    private getBodyValidatorsForMessageType(type: string) {
        switch (type) {
            case Message.TYPE_NORMAL:
                return [Validators.required, Validators.maxLength(800), () => {
                    if (this.validateInput()) {
                        return null;
                    }

                    return { validateVariables : { valid: false } };
                }];
            default:
                return [Validators.maxLength(800)];
        }
    }

    private getAttachmentDataValidatorsForType(type: string) {
        switch (type) {
            case Attachment.TYPE_CUSTOM:
                return [Validators.required];
            default:
                return [];
        }
    }

    private resetAttachment() {
        // Clear native element for re-uploading same image
        if (this.attachmentInput) {
            this.attachmentInput.nativeElement.value = null;
        }

        if (this.oldAttachment) {
            this.attachmentChanged = true;
        }

        // Clear actually form value
        this.messageForm.controls['attachmentData'].setValue(null);

        // Clear attachment flags and values
        this.attachment.current = {
            error  : null,
            mime   : null,
            size   : null,
            width  : null,
            height : null,
            filename : null
        };
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/messages/components/message.component.ts